import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconPower, IconTrash } from "@tabler/icons-react";
import { useCallback } from "react";
import type { AddendumMacro } from "../../../services/addendum.service";
import { stripHtml } from "../../../utils/helpers";

interface AddendumMacroTableRowProps {
  macro: AddendumMacro;
  onEdit: (macro: AddendumMacro) => void;
  onToggle: (macro: AddendumMacro) => void;
  onDelete: (macro: AddendumMacro) => void;
}

export function AddendumMacroTableRow({
  macro,
  onEdit,
  onToggle,
  onDelete,
}: AddendumMacroTableRowProps) {
  const handleEdit = useCallback(() => onEdit(macro), [macro, onEdit]);
  const handleToggle = useCallback(() => onToggle(macro), [macro, onToggle]);
  const handleDelete = useCallback(() => onDelete(macro), [macro, onDelete]);

  return (
    <Table.Tr style={{ opacity: macro.isActive ? 1 : 0.6 }}>
      <Table.Td>
        <Text size="sm" fw={600}>
          {macro.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {macro.shortcut || "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge
          color={macro.macroType === "amendment" ? "orange" : "cyan"}
          variant="light"
        >
          {macro.macroType === "amendment" ? "Amendment" : "Addendum"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={2}>
          {stripHtml(macro.contentHtml)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={macro.isActive ? "green" : "gray"} variant="light">
          {macro.isActive ? "Active" : "Inactive"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label="Edit macro">
            <ActionIcon variant="subtle" onClick={handleEdit}>
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={macro.isActive ? "Deactivate macro" : "Activate macro"}
          >
            <ActionIcon
              variant="subtle"
              color={macro.isActive ? "orange" : "green"}
              onClick={handleToggle}
            >
              <IconPower size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete macro">
            <ActionIcon variant="subtle" color="red" onClick={handleDelete}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
